// Submarine — a chubby storybook submarine resting on its belly like a bath
// toy hauled out of the tub: a fat yellow hull with round cream portholes, a
// conning tower with a periscope, cross tail fins and a coral propeller.
// Toy-like: plump silhouette, oversized rivet rings, warm flat colours.
//
// Frame: y = 0 is the ground (the belly skids touch it), +Z is the nose.
// About 2.2 m long over a 1x2 tile pad.
//
// Animation layout (flat nodes, deliberate pivots):
//   - "hull" : the whole boat except the propeller. Bobs and rolls gently in
//              idle, as if remembering the sea. Pivot on the hull's long axis.
//   - "prop" : hub + three blades at the tail, spinning about the Z axis. It
//              sits on the hull's roll axis and mirrors the idle bob, so the
//              two nodes never drift apart.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const YELLOW = "#f0b429";      // main hull — warm toy yellow
const YELLOW_DARK = "#cf951a"; // shaded hull (tail cone, tower base, skids)
const CREAM = "#f6efe3";       // trim: porthole rings, tower cap, deck stripe
const GLASS = "#8fd3df";       // porthole glass — friendly sky teal
const CORAL = "#d9534a";       // propeller + waterline stripe accent
const DARK = "#5c5350";        // periscope, prop hub (warm dark, never black)

// Frame measures.
const HULL_R = 0.40;           // hull tube radius; the axis runs at this height
const AXIS_Y = HULL_R;         // the hull's long axis height
const PROP_Z = -1.16;          // propeller plane
const HULL_PIVOT: [number, number, number] = [0, AXIS_Y, 0];
const PROP_PIVOT: [number, number, number] = [0, AXIS_Y, PROP_Z];

// A porthole on one flank: cream ring plus teal glass disc, facing +-X.
function porthole(x: number, z: number): Part[] {
  const s = Math.sign(x);
  return [
    { geometry: new THREE.CylinderGeometry(0.095, 0.095, 0.05, 12), color: CREAM,
      position: [x, AXIS_Y + 0.06, z], rotation: [0, 0, Math.PI / 2], name: "hull" },
    { geometry: new THREE.CylinderGeometry(0.062, 0.062, 0.03, 12), color: GLASS,
      position: [x + s * 0.02, AXIS_Y + 0.06, z], rotation: [0, 0, Math.PI / 2], name: "hull" },
  ];
}

function submarineParts(): Part[] {
  const parts: Part[] = [
    // Belly skids: two dark runners that seat the round hull on the ground.
    { geometry: new THREE.BoxGeometry(0.14, 0.08, 1.05), color: YELLOW_DARK,
      position: [-0.16, 0.04, 0.05], name: "hull" },
    { geometry: new THREE.BoxGeometry(0.14, 0.08, 1.05), color: YELLOW_DARK,
      position: [0.16, 0.04, 0.05], name: "hull" },

    // The fat hull tube along Z, with a round nose and a tapering tail cone.
    { geometry: new THREE.CylinderGeometry(HULL_R, HULL_R, 1.2, 16), color: YELLOW,
      position: [0, AXIS_Y, 0.05], rotation: [Math.PI / 2, 0, 0], name: "hull" },
    { geometry: new THREE.SphereGeometry(HULL_R, 14, 10), color: YELLOW,
      position: [0, AXIS_Y, 0.65], scale: [1, 1, 1.15], name: "hull" },
    // Tail cone: wide end (+Y after the rotation maps to +Z) meets the tube.
    { geometry: new THREE.CylinderGeometry(0.40, 0.15, 0.55, 14), color: YELLOW_DARK,
      position: [0, AXIS_Y, -0.83], rotation: [Math.PI / 2, 0, 0], name: "hull" },

    // Coral waterline stripe along each flank.
    { geometry: new THREE.BoxGeometry(0.03, 0.09, 1.3), color: CORAL,
      position: [-0.395, AXIS_Y - 0.1, 0.05], name: "hull" },
    { geometry: new THREE.BoxGeometry(0.03, 0.09, 1.3), color: CORAL,
      position: [0.395, AXIS_Y - 0.1, 0.05], name: "hull" },

    // Portholes: three per side down the flanks.
    ...porthole(-0.385, 0.45), ...porthole(-0.385, 0.1), ...porthole(-0.385, -0.25),
    ...porthole(0.385, 0.45), ...porthole(0.385, 0.1), ...porthole(0.385, -0.25),

    // Conning tower: a stout drum with a cream cap, set forward of centre.
    { geometry: new THREE.CylinderGeometry(0.17, 0.20, 0.32, 12), color: YELLOW_DARK,
      position: [0, 0.88, 0.18], name: "hull" },
    { geometry: new THREE.CylinderGeometry(0.19, 0.19, 0.07, 12), color: CREAM,
      position: [0, 1.06, 0.18], name: "hull" },
    // Periscope: a dark stalk with a forward-looking eye tube.
    { geometry: new THREE.CylinderGeometry(0.035, 0.035, 0.28, 8), color: DARK,
      position: [0, 1.2, 0.18], name: "hull" },
    { geometry: new THREE.CylinderGeometry(0.045, 0.045, 0.14, 8), color: DARK,
      position: [0, 1.34, 0.24], rotation: [Math.PI / 2, 0, 0], name: "hull" },
    { geometry: new THREE.CylinderGeometry(0.032, 0.032, 0.02, 8), color: GLASS,
      position: [0, 1.34, 0.315], rotation: [Math.PI / 2, 0, 0], name: "hull" },

    // Tail fins: a vertical rudder and a horizontal dive plane, crossed.
    { geometry: new THREE.BoxGeometry(0.06, 0.62, 0.3), color: YELLOW,
      position: [0, AXIS_Y + 0.12, -0.82], name: "hull" },
    { geometry: new THREE.BoxGeometry(0.85, 0.06, 0.3), color: YELLOW,
      position: [0, AXIS_Y, -0.82], name: "hull" },

    // Propeller: dark hub + three coral blades, all named "prop" so they spin
    // together about the Z axis.
    { geometry: new THREE.SphereGeometry(0.075, 8, 6), color: DARK,
      position: [0, AXIS_Y, PROP_Z], scale: [1, 1, 1.4], name: "prop" },
  ];

  // Three blades, splayed at 120 degrees about the shaft.
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    parts.push({
      geometry: new THREE.BoxGeometry(0.09, 0.3, 0.035),
      color: CORAL,
      position: [-Math.sin(a) * 0.17, AXIS_Y + Math.cos(a) * 0.17, PROP_Z],
      rotation: [0, 0, a],
      name: "prop",
    });
  }
  return parts;
}

export const submarine = defineModel({
  id: "submarine",
  batchId: "mess",
  name: "Submarine",
  tags: ["mess", "cute", "animated", "vehicle", "submarine", "sea", "boat", "toy"],
  summary: "A chubby yellow toy submarine on its belly, propeller ready to spin.",
  description:
    "A storybook submarine hauled out on dry land: a fat yellow hull resting on " +
    "two belly skids, round cream portholes with teal glass down each flank, a " +
    "conning tower with a periscope, crossed tail fins and a three-blade coral " +
    "propeller. It bobs and rolls gently in idle as if remembering the sea, and " +
    "the propeller spins about the Z axis in its signature move.",
  author: "models",
  footprint: 0.75,
  tiles: { w: 1, h: 2 },
  parts: submarineParts,
  animations: [
    // Idle — a dockside daydream: the whole boat rolls a whisker about its long
    // axis and breathes up and down. The propeller mirrors the same track (it
    // sits on the roll axis), so the two nodes stay glued together.
    {
      id: "idle",
      label: "Idle bob",
      duration: 3.4,
      loop: true,
      tracks: [
        {
          target: "hull", pivot: HULL_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0.03], ease: "sine" },
            { t: 1.7, value: [0, 0, -0.03], ease: "sine" },
            { t: 3.4, value: [0, 0, 0.03], ease: "sine" },
          ],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.7, value: [0, 0.02, 0], ease: "sine" },
            { t: 3.4, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "prop", pivot: HULL_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0.03], ease: "sine" },
            { t: 1.7, value: [0, 0, -0.03], ease: "sine" },
            { t: 3.4, value: [0, 0, 0.03], ease: "sine" },
          ],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.7, value: [0, 0.02, 0], ease: "sine" },
            { t: 3.4, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // Propeller spin — the signature move: the whole three-blade assembly turns
    // steadily about the Z shaft.
    {
      id: "prop-spin",
      label: "Propeller spin",
      duration: 1.2,
      loop: true,
      tracks: [
        {
          target: "prop", pivot: PROP_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0] },
            { t: 0.4, value: [0, 0, (Math.PI * 2) / 3] },
            { t: 0.8, value: [0, 0, (Math.PI * 4) / 3] },
            { t: 1.2, value: [0, 0, Math.PI * 2] },
          ],
        },
      ],
    },
  ],
});
